/**
 * 74.19's yards (DECISIONS 438; the coordinator's design review, Nov 3): the erosion carve laid last, with what the setup
 * placed held.
 *
 * The carve is big (to 39 people on the Story's light dial) and the setup sites its bases, buildings and props by the ground:
 * carved first, 9 of 48 Story bases slid to other spots and a base's generator stood 17 people down a gully cut through its
 * yard (bar 4 of the light-vs-off A/B). So the generator keeps the carve aside (`PlanetState.carved`, core/landforms.ts
 * plannedLandforms, core/erosionCarve.ts plannedErosion): everything in mapgen and the setup is placed on the dial off's
 * planet, to the bit — the same sites whatever the dial — and the setup's last step (`layCarve`) lays the carve as one
 * terrain-log record a planet (`kind: 'carve'`), applied through applyTerrainEdit:
 *
 * - **The yards**: each board base's disc (`basePeople`), every structure's footprint and a margin (`marginPeople`), every
 *   terrace's reach and a margin (`terracePeople`). Inside a yard the ground stays as the setup left it — the dial off's,
 *   with its terraces.
 * - **The rim**: past a yard's edge the carve is held to `grade` (TERRACE.rampBatter: the grade a pool's ramp gives wheels;
 *   the walk's limit is 1.2) — `h + clamp(carve, ±grade·d)`, d the distance past the nearest yard — and laid whole where the
 *   grade reaches it. Where the setup left the ground as generated, the carved height is laid exactly (the records' levels
 *   equal the mesh to the bit, as landformsOf laid them).
 * - **The records**: the carved landform records replace the planned ones, cleared again under the structures
 *   (core/landformSites.ts) and, lane C's guard, wherever one reaches a yard (core/carveLay.ts recordsClearOfYards); a
 *   vertex whose radius is a kept record's level takes the whole carve (no yard moves a vertex a record reads, and no kept
 *   record's vertex is in a yard).
 * - **The rest**: the generated heights (`initialHeightmap`) take the carve too — the route graph, the world sheet and its
 *   drainage, the woods, sight and the kernel read the carved ground in play; the planned heights are kept
 *   (`plannedHeightmap`) for what must stay the setup's (planetSettlements); the forest's basis moves to the carved ground; a
 *   world without the landforms keeps its key (`carvedGroundKey`).
 *
 * The dial off: no `carved`, nothing runs — the planet and the log as before, to the bit.
 */
import type { GameState, PlanetState, TerrainEdit, Vec3 } from '../types';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { structureFootprint } from './buildingScale';
import { terraceReach } from './terraceGround';
import { clearShapingsUnder } from './landformSites';
import { rebaseForest } from './forestHeld';
import { withCarve, recordsClearOfYards } from './carveLay';

export const CARVE_YARDS = {
  /** a board base's yard, people round its spot (its HQ, pad, dump and vehicles stand within 31 on the Story's 24 seeds) */
  basePeople: 35,
  /** past a structure's footprint (its hitbox's half extent) */
  marginPeople: 4,
  /** past a terrace's reach (its pad, band and ramps) */
  terracePeople: 2,
  /** the rim's grade, rise over run: TERRACE.rampBatter's (a literal: no constant read across modules at load; a test holds them equal) */
  grade: 0.6,
};

export interface CarveYard { dir: Vec3; radius: number }

/** The yards on planet `pi` (world units): the board's bases, every structure, every terrace. */
export function carveYardsOf(state: GameState, pi: number): CarveYard[] {
  const planet = state.planets[pi], person = PERSON_BODY_HEIGHT * personSetOf(state).k, out: CarveYard[] = [];
  for (const b of state.board?.bases ?? []) if (b.planetIndex === pi) out.push({ dir: { ...b.dir }, radius: CARVE_YARDS.basePeople * person });
  for (const s of state.structures) {
    if (s.planetIndex !== pi) continue;
    out.push({ dir: { ...s.surfaceDir }, radius: structureFootprint(s, state.config, planet.radius) + CARVE_YARDS.marginPeople * person });
  }
  for (const t of planet.terraces ?? []) out.push({ dir: { ...t.dir }, radius: terraceReach(t) + CARVE_YARDS.terracePeople * person });
  return out;
}

/** The record that lays planet `pi`'s carve with `yards` held. */
export function carveEdit(pi: number, planet: PlanetState, yards: CarveYard[]): TerrainEdit {
  // (its reach the whole world — what it changes — from a point on the surface: the log's readers place an edit by its point)
  const P = planet.position, R = planet.radius;
  return { kind: 'carve', planetIndex: pi, planetId: planet.id, point: { x: P.x, y: P.y + R, z: P.z }, radius: Math.PI * R, depth: 0, carve: { yards, grade: CARVE_YARDS.grade } };
}

/**
 * The setup's last step: each carved planet's carve laid, its yards held (one record in the terrain log), the carved records
 * cleared under the structures, the forest's basis moved onto the carved ground. No carved planet: the state unchanged.
 */
export function layCarve(state: GameState): GameState {
  if (!state.planets.some(p => p.carved)) return state;
  const edits: TerrainEdit[] = [];
  // (the kept records first: the carved ones, cleared under the structures as the planned ones were)
  const swapped = clearShapingsUnder({ ...state, planets: state.planets.map(p => (p.carved ? { ...p, shapings: p.carved.shapings.length ? p.carved.shapings : undefined } : p)) });
  const planets = swapped.planets.map((p, pi) => {
    if (!p.carved) return p;
    const e = carveEdit(pi, p, carveYardsOf(state, pi));
    edits.push(e);
    // (lane C: and none reaching a yard — core/carveLay.ts recordsClearOfYards)
    const kept = recordsClearOfYards(p, p.shapings ?? [], e.carve!.yards);
    return withCarve({ ...p, shapings: kept.length ? kept : undefined, carved: { ...p.carved, shapings: kept } }, e);
  });
  let out: GameState = { ...state, planets, terrainEdits: [...(state.terrainEdits ?? []), ...edits] };
  for (const e of edits) out = rebaseForest(out, e.planetIndex);
  return out;
}
